import React, { useRef, useState, useEffect } from "react";
import { Upload, X } from "lucide-react";
import toast from "../../utils/notify";

const FileUpload = ({
  value,
  onChange,
  disabled = false,
  label,
  required = false,
  helperText,
  allowedTypes = [],
  error   
}) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    // If value is a URL (existing file from backend)
    if (typeof value === "string") {
      setFileName(value.split("/").pop());
      if (value.match(/\.(jpeg|jpg|png|webp|svg)$/i)) {
        setPreview(value);
      } else {
        setPreview(null);
      }
    }

    // If value is a File object
    if (value instanceof File) {
      setFileName(value.name);

      if (value.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(value);
      } else {
        setPreview(null);
      }
    }

    if (!value) {
      setPreview(null);
      setFileName("");
    }
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      toast.error("Invalid file type selected.");
      return;
    }

    onChange(file);
  };

  const handleRemove = () => {
    setPreview(null);
    setFileName("");
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {helperText && (
        <p className="text-xs text-gray-500">{helperText}</p>
      )}

      <div
        className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 bg-white transition
          ${disabled
            ? "bg-gray-100 border-gray-300 cursor-not-allowed"
            : error
              ? "border-red-500 focus-within:ring-0"
              : "border-gray-300 focus-within:border-gray-400 focus-within:ring-0"
          }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={allowedTypes.join(",")}
          className="hidden"
          disabled={disabled}
        />

        <div
          className={`flex-1 text-sm truncate ${fileName ? "text-gray-700" : "text-gray-400"
            }`}
        >
          {fileName || "Select file"}
        </div>

        {!disabled && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-600 hover:text-blue-700"
            >
              <Upload className="w-4 h-4" />
            </button>

            {fileName && (
              <button
                type="button"
                onClick={handleRemove}
                className="text-red-500 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Image preview only */}
      {preview && (
        <div className="mt-2">
          <img
            src={preview}
            alt="Preview"
            className="h-16 object-contain rounded border"
          />
        </div>
      )}
    </div>
  );
};

export default FileUpload;
